import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

import {
  FilePlus,
  Eye,
  Search,
  ShieldCheck,
  UserCog,
  LogOut,
  Archive,
  FolderSearch,
  Menu,
  Bell,
} from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [role, setRole] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setRole(userDoc.data().role);
        }
      }
    };

    const fetchNotifications = async () => {
      if (currentUser) {
        const q = query(
          collection(db, 'notifications'),
          where('userId', '==', currentUser.uid),
          where('read', '==', false)
        );
        const snapshot = await getDocs(q);
        setNotificationCount(snapshot.size);
      }
    };

    fetchUserRole();
    fetchNotifications();
  }, [currentUser]);

  const isActive = (path) => location.pathname === path;

  // Core user routes
  const userRoutes = [
    { icon: FilePlus, label: 'Report Item', path: '/report-lost' },
    { icon: Eye, label: 'Items Collection', path: '/item-collection' },
    { icon: Search, label:'View reported items', path: '/track-claim' },
    { icon: FolderSearch, label: 'Lost Items', path: '/lost-items' },
    { icon: Archive, label: 'Found Items', path: '/found-items' },
    {
      icon: Bell,
      label: 'Notifications',
      path: '/notifications',
      badge: notificationCount > 0 ? notificationCount : null,
    },
  ];

  // Admin only route
  const adminRoutes = role === 'admin'
    ? [{ icon: UserCog, label: 'Admin Dashboard', path: '/admin-dashboard' }]
    : [];

  // Security only route
  const securityRoutes = role === 'security'
    ? [{ icon: ShieldCheck, label: 'Security Dashboard', path: '/security-dashboard' }]
    : [];

  // ✅ Client Dashboard is now always visible to all users
  const clientRoutes = [
    { icon: UserCog, label: 'Client Dashboard', path: '/client-dashboard' }
  ];

  return (
    <aside
      style={{
        width: collapsed ? '80px' : '250px',
        backgroundColor: '#1C2535',
        padding: '1.5rem 1rem',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        fontFamily: 'Segoe UI, sans-serif',
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        transition: 'width 0.3s ease',
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          background: '#ffffff',
          color: '#1C2535',
          border: 'none',
          borderRadius: '8px',
          padding: '0.5rem',
          cursor: 'pointer',
          fontSize: '1rem',
          alignSelf: collapsed ? 'center' : 'flex-end',
          marginBottom: '1.2rem',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <Menu />
      </button>

      {/* Branding */}
      {!collapsed && (
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem' }}>📦</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>Asset Recovery</div>
        </div>
      )}

      {/* Render Section */}
      <SidebarSection title="Main" collapsed={collapsed} routes={userRoutes} isActive={isActive} navigate={navigate} />
      {securityRoutes.length > 0 && <SidebarSection title="Security" collapsed={collapsed} routes={securityRoutes} isActive={isActive} navigate={navigate} />}
      {adminRoutes.length > 0 && <SidebarSection title="Admin" collapsed={collapsed} routes={adminRoutes} isActive={isActive} navigate={navigate} />}
      <SidebarSection title="Client" collapsed={collapsed} routes={clientRoutes} isActive={isActive} navigate={navigate} />

      {/* Logout Button */}
      <div style={{ marginTop: 'auto' }}>
        <button
          title={collapsed ? 'Logout' : ''}
          onClick={async () => {
            try {
              await logout();
              navigate('/login');
            } catch (error) {
              console.error('Logout failed:', error);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? '0' : '0.9rem',
            padding: '0.75rem 1rem',
            background: '#F44336',
            color: 'white',
            borderRadius: '12px',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            justifyContent: collapsed ? 'center' : 'flex-start',
            boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
            marginTop: '1.5rem',
          }}
        >
          <LogOut size={20} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

// ✅ Reusable section for grouped routes
const SidebarSection = ({ title, collapsed, routes, isActive, navigate }) => (
  <>
    {!collapsed && routes.length > 0 && (
      <div style={{ color: '#B0C4DE', fontSize: '0.8rem', margin: '1rem 0 0.5rem 0.5rem', textTransform: 'uppercase' }}>
        {title}
      </div>
    )}
    {routes.map(({ icon: Icon, label, path, badge }) => (
      <button
        key={path}
        title={collapsed ? label : ''}
        onClick={() => navigate(path)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? '0' : '0.9rem',
          padding: '0.75rem 1rem',
          background: isActive(path)
            ? 'linear-gradient(135deg, #B4D4FF, #DCEEFF)'
            : '#ffffff',
          color: '#1C2535',
          borderRadius: '12px',
          fontSize: '0.95rem',
          fontWeight: '500',
          border: 'none',
          cursor: 'pointer',
          justifyContent: collapsed ? 'center' : 'flex-start',
          boxShadow: isActive(path)
            ? '0 4px 10px rgba(0, 0, 0, 0.15)'
            : '0 2px 5px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div
          style={{
            background: isActive(path)
              ? '#ffffff'
              : 'linear-gradient(135deg, #E1F0FF, #F0F7FF)',
            padding: '0.5rem',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={20} color="#1C2535" />
        </div>
        {!collapsed && (
          <>
            <span>{label}</span>
            {badge && (
              <span
                style={{
                  background: 'red',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '0 6px',
                  fontSize: '0.75rem',
                  marginLeft: 'auto',
                  fontWeight: 'bold',
                }}
              >
                {badge}
              </span>
            )}
          </>
        )}
      </button>
    ))}
  </>
);

export default Sidebar;
